/**
 * Client-side encryption for API key storage using Web Crypto API.
 *
 * SECURITY WARNING:
 * Client-side encryption provides OBFUSCATION only, not true security.
 * The encryption key is visible in the JavaScript bundle, making it possible
 * for determined attackers to decrypt stored data.
 *
 * This implementation:
 * - Prevents casual snooping of localStorage in browser DevTools
 * - Adds a layer of defense against basic XSS attacks
 * - Should NOT be relied upon for truly sensitive data
 *
 * For production systems handling sensitive API keys, consider:
 * - Using a server-side proxy for AI API calls
 * - Implementing proper backend key management
 * - Using secure session-based key storage
 *
 * Benefits of Web Crypto API over crypto-js:
 * - Native browser implementation (no supply chain risk)
 * - Hardware-accelerated when available
 * - Uses AES-GCM (authenticated encryption) for integrity protection
 */

const ENCRYPTION_VERSION = 2; // Version 2 = Web Crypto API format
const SALT = new TextEncoder().encode('voxpop-salt-v2');
const ITERATIONS = 100000; // PBKDF2 iterations (increased for security)

/**
 * Derive an encryption key from a password using PBKDF2.
 */
const deriveKey = async (password: string): Promise<CryptoKey> => {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: SALT,
      iterations: ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Get the base encryption password from environment or fallback.
 */
const getBasePassword = (): string => {
  const envKey = import.meta.env.VITE_ENCRYPTION_KEY;

  if (!envKey && import.meta.env.DEV) {
    console.warn(
      '[Security] VITE_ENCRYPTION_KEY not set. Using fallback key. ' +
        'Set a unique key in .env for better security.'
    );
  }

  return envKey || 'voxpop-local-encryption-key-v2';
};

// Cache the derived key to avoid re-deriving on every operation
let cachedKey: CryptoKey | null = null;
let cachedKeyPassword: string | null = null;

const getEncryptionKey = async (): Promise<CryptoKey> => {
  const password = getBasePassword();

  if (cachedKey && cachedKeyPassword === password) {
    return cachedKey;
  }

  cachedKey = await deriveKey(password);
  cachedKeyPassword = password;
  return cachedKey;
};

/**
 * Convert ArrayBuffer to base64 string.
 */
const bufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Convert base64 string to ArrayBuffer.
 */
const base64ToBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Encrypt sensitive data for localStorage storage.
 *
 * @param text - The plaintext to encrypt
 * @returns Encrypted ciphertext, or empty string if input is empty
 * @throws Error if encryption fails (never returns plaintext on failure)
 */
export const encryptData = async (text: string): Promise<string> => {
  if (!text) return '';

  try {
    const key = await getEncryptionKey();
    const encoder = new TextEncoder();

    // Generate random IV for each encryption (critical for AES-GCM security)
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Add metadata to detect tampering and enable future versioning
    const payload = JSON.stringify({
      v: ENCRYPTION_VERSION,
      d: text,
      t: Date.now(),
    });

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoder.encode(payload)
    );

    // Combine IV + ciphertext and encode as base64
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    // Prefix with version marker for format detection
    return `v2:${bufferToBase64(combined.buffer)}`;
  } catch (error) {
    // SECURITY: Never fall back to plaintext - fail safely
    console.error('[Security] Encryption failed:', error);
    throw new Error('Failed to encrypt sensitive data');
  }
};

/**
 * Decrypt data from localStorage.
 *
 * @param cipherText - The encrypted ciphertext
 * @returns Decrypted plaintext
 * @throws Error if decryption fails (never returns ciphertext on failure)
 */
export const decryptData = async (
  cipherText: string,
  options?: { suppressLog?: boolean },
): Promise<string> => {
  if (!cipherText) return '';

  try {
    // Check for v2 format (Web Crypto API)
    if (!cipherText.startsWith('v2:')) {
      throw new Error(
        'Legacy encryption format detected. Please re-enter your API key.'
      );
    }

    const key = await getEncryptionKey();
    const decoder = new TextDecoder();

    // Remove version prefix and decode
    const combined = new Uint8Array(base64ToBuffer(cipherText.slice(3)));

    // Extract IV (first 12 bytes) and ciphertext
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );

    const payload = JSON.parse(decoder.decode(decrypted));

    if (payload.v === ENCRYPTION_VERSION && payload.d) {
      return payload.d;
    }

    throw new Error('Invalid payload format');
  } catch (error) {
    // SECURITY: Don't return ciphertext - could be injection attempt
    if (!options?.suppressLog) {
      console.error('[Security] Decryption failed:', error);
    }
    throw new Error('Failed to decrypt data. Data may be corrupted or from old format.');
  }
};

/**
 * Check if a string appears to be encrypted data.
 * Useful for migration scenarios.
 *
 * @param data - String to check
 * @returns True if the string appears to be encrypted
 */
export const isEncrypted = (data: string): boolean => {
  if (!data) return false;

  // v2 format (Web Crypto API)
  if (data.startsWith('v2:') && data.length > 20) {
    return true;
  }

  // Legacy format (crypto-js OpenSSL format starts with "U2F" = base64 for "Sal")
  if (data.startsWith('U2F') && data.length > 20) {
    return true;
  }

  return false;
};

/**
 * Check if data is in legacy (crypto-js) format.
 * Used to prompt user to re-enter their API key.
 *
 * @param data - String to check
 * @returns True if the string is in legacy format
 */
export const isLegacyFormat = (data: string): boolean => {
  return data.startsWith('U2F') && data.length > 20;
};

/**
 * Safely attempt to decrypt, returning null on failure instead of throwing.
 * Useful for optional decryption attempts.
 *
 * @param cipherText - The encrypted ciphertext
 * @returns Decrypted plaintext or null if decryption fails
 */
export const tryDecryptData = async (cipherText: string): Promise<string | null> => {
  try {
    return await decryptData(cipherText, { suppressLog: true });
  } catch {
    return null;
  }
};
